// DailySummary.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }
  return currency;
};

// ✅ Format a date as MM/DD/YYYY
const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  } catch {
    return dateStr;
  }
};

const DailySummary = () => {
  const [summary, setSummary] = useState({
    purchases: 0,
    sales: 0,
    expenses: 0,
    totalCOGS: 0,
    totalVat: 0,
    totalDiscount: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    salesCount: 0,
    purchasesCount: 0,
    expensesCount: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState('all');
  const [types, setTypes] = useState({
    sale: true,
    purchase: true,
    expense: true
  });

  const printRef = useRef();

  const formatCurrency = useCallback((amount) => {
    const currencyCode = getCompanyCurrency(company);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [company]);

  const fetchCompany = useCallback(async () => {
    try {
      setLoadingCompany(true);
      const res = await axios.get(`${url}/api/company/current`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      setCompany(res.data);
    } catch (err) {
      setCompany(null);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

 // ✅ FIX: Net Sales = Subtotal - Discounts (Excludes Tax & Shipping)
 // AND correctly subtract returns
 const fetchSales = async (startDate, endDate) => {
  try {
    const params = { limit: 5000 };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const res = await axios.get(`${url}/api/sales/invoices`, {
      headers: getAuthHeaders(),
      params,
    });
    const invoices = res.data.data || [];
    
    let netSales = 0;
    let totalCOGS = 0;
    let totalDiscount = 0;
    let totalVat = 0;
    let totalPaid = 0;
    let totalOutstanding = 0;

    invoices.forEach(inv => {
      const sign = inv.is_return ? -1 : 1;
      const subTotal = Number(inv.sub_total || 0);
      const discount = Number(inv.total_discount || 0);
      
      netSales += sign * (subTotal - discount);
      totalDiscount += sign * discount;
      totalVat += sign * Number(inv.total_tax || 0);
      totalPaid += sign * Number(inv.total_amount_paid || 0);
      totalOutstanding += sign * Number(inv.net_amount || 0);

      const invCogs = (inv.sales_transactions || []).reduce((sum, line) => sum + Number(line.inventory_total_cost || 0), 0);
      totalCOGS += sign * invCogs;
    });

    return { total: netSales, count: invoices.length, vat: totalVat, discount: totalDiscount, paid: totalPaid, outstanding: totalOutstanding, cogs: totalCOGS };
  } catch (err) {
    return { total: 0, count: 0, vat: 0, discount: 0, paid: 0, outstanding: 0, cogs: 0 };
  }
};

  const fetchPurchases = async (startDate, endDate) => {
    try {
      const params = { limit: 5000 };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${url}/api/purchase/invoices`, {
        headers: getAuthHeaders(),
        params,
      });
      const invoices = res.data.data || [];
      const total = invoices.reduce((sum, inv) => sum + Number(inv.grand_total || 0), 0);
      const vat = invoices.reduce((sum, inv) => sum + Number(inv.total_tax || inv.tax_amount || 0), 0);
      const discount = invoices.reduce((sum, inv) => sum + Number(inv.total_discount || inv.discount_amount || 0), 0);
      const paid = invoices.reduce((sum, inv) => sum + Number(inv.total_amount_paid || 0), 0);
      const outstanding = invoices.reduce((sum, inv) => {
        if (inv.net_amount !== undefined && inv.net_amount !== null) {
          return sum + Number(inv.net_amount || 0);
        }
        return sum + Math.max(Number(inv.grand_total || 0) - Number(inv.total_amount_paid || 0), 0);
      }, 0);
      return { total, count: invoices.length, vat, discount, paid, outstanding };
    } catch (err) {
      return { total: 0, count: 0, vat: 0, discount: 0, paid: 0, outstanding: 0 };
    }
  };

  const fetchExpenses = async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${url}/api/expense`, {
        headers: getAuthHeaders(),
        params,
      });
      const expenses = res.data.data || [];
      const total = expenses.reduce((sum, exp) => sum + Number(exp.amount || 0), 0);
      return { total, count: expenses.length };
    } catch (err) {
      return { total: 0, count: 0 };
    }
  };

  const fetchSummary = useCallback(async (overrideStart, overrideEnd) => {
    setLoading(true);
    setError(null);
    try {
      const start = overrideStart !== undefined ? overrideStart : (fromDate || undefined);
      const end = overrideEnd !== undefined ? overrideEnd : (toDate || undefined);

      const [sales, purchases, expenses] = await Promise.all([
        types.sale ? fetchSales(start, end) : Promise.resolve({ total: 0, count: 0, vat: 0, discount: 0, paid: 0, outstanding: 0, cogs: 0 }),
        types.purchase ? fetchPurchases(start, end) : Promise.resolve({ total: 0, count: 0, vat: 0, discount: 0, paid: 0, outstanding: 0 }),
        types.expense ? fetchExpenses(start, end) : Promise.resolve({ total: 0, count: 0 }),
      ]);

      setSummary({
        purchases: purchases.total || 0,
        sales: sales.total || 0,
        expenses: expenses.total || 0,
        totalCOGS: sales.cogs || 0,
        totalVat: (sales.vat || 0) + (purchases.vat || 0),
        totalDiscount: (sales.discount || 0) + (purchases.discount || 0),
        totalPaid: (sales.paid || 0) + (purchases.paid || 0),
        totalOutstanding: (sales.outstanding || 0) + (purchases.outstanding || 0),
        salesCount: sales.count || 0,
        purchasesCount: purchases.count || 0,
        expensesCount: expenses.count || 0,
      });
    } catch (err) {
      setError('Failed to load daily summary');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, types]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleGenerate = () => fetchSummary();

  // ✅ Quick Date Filter Handler
  const handleQuickDate = (days) => {
    setActiveQuickFilter(days);

    if (days === 'all') {
      setFromDate('');
      setToDate('');
      fetchSummary(undefined, undefined);
      return;
    }

    const d = new Date();
    d.setDate(d.getDate() + days);

    const offset = d.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(d - offset)).toISOString().split('T')[0];

    setFromDate(localISOTime);
    setToDate(localISOTime);
    fetchSummary(localISOTime, localISOTime);
  };

  // ✅ Single print handler — uses the browser's native print
  const handlePrint = () => window.print();

  const logoUrl = company?.logo
    ? company.logo.startsWith('http') ? company.logo : `${url}/${company.logo.replace(/^\/+/, '')}`
    : null;

  const primaryColor = '#2e3192';
  const secondaryColor = '#f1592a';

  // ✅ Address + Phone 1 · 2 · 3
  const companyAddress = company?.admin_user?.Usercomp_address || company?.address || '';
  const companyContact = company?.admin_user?.Usercomp_contact || company?.contact || '';
  const companyContact2 = company?.admin_user?.Usercomp_contact_2 || company?.contact_2 || '';
  const companyContact3 = company?.admin_user?.Usercomp_contact_3 || company?.contact_3 || '';
  const companyEmail = company?.admin_user?.Usercomp_email || company?.admin_email || '';
  const phoneLine = [companyContact, companyContact2, companyContact3].filter(Boolean).join(' · ');

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const companyCurrency = getCompanyCurrency(company);

  // ✅ Date range label for the header
  const rangeLabel = (() => {
    if (fromDate && toDate) return `${formatDateShort(fromDate)} - ${formatDateShort(toDate)}`;
    if (fromDate) return `From ${formatDateShort(fromDate)}`;
    if (toDate) return `Until ${formatDateShort(toDate)}`;
    return 'All Time';
  })();

  const hasData =
    summary.purchases !== 0 ||
    summary.sales !== 0 ||
    summary.expenses !== 0 ||
    summary.totalVat !== 0 ||
    summary.totalDiscount !== 0 ||
    summary.totalPaid !== 0 ||
    summary.totalOutstanding !== 0;

  // ✅ Profit Calculations
  const grossProfit = summary.sales - summary.totalCOGS;
  const netProfit = grossProfit - summary.expenses;

  if (loadingCompany) {
    return <div style={{ textAlign: 'center', marginTop: '2rem' }}>Loading company data...</div>;
  }

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '1.5rem' }}>
      {/* ==================================================
          SCREEN PREVIEW
      ================================================== */}
      <div className="screen-only" ref={printRef}>
        {/* HEADER */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '1.5rem', marginBottom: '1.5rem', borderLeft: `4px solid ${primaryColor}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {logoUrl && (
                <img
                  src={logoUrl}
                  alt="Logo"
                  style={{ height: '64px', maxWidth: '80px', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: primaryColor }}>{company?.company_name || 'Company Name'}</h1>
                <p style={{ color: '#6c757d', fontSize: '0.875rem' }}>{company?.business_type?.name || 'Business'} | Code: {company?.company_code || 'N/A'}</p>
              </div>
            </div>
            <div style={{ textAlign: 'right', color: '#495057', fontSize: '0.875rem' }}>
              <p>{companyAddress || 'No address'}</p>
              <p style={{ marginTop: '0.25rem' }}>
                📞 {companyContact || 'No phone'}
                {companyContact2 ? ` · ${companyContact2}` : ''}
                {companyContact3 ? ` · ${companyContact3}` : ''}
                <br />
                ✉️ {companyEmail || 'No email'}
              </p>
              <p>Fiscal year start: {company?.fiscal_year_start ? new Date(company.fiscal_year_start).toLocaleDateString() : 'Not set'}</p>
            </div>
          </div>
          <div style={{ marginTop: '1rem', borderTop: '1px solid #e9ecef', paddingTop: '0.5rem' }}>
            <h2 style={{ textAlign: 'center', color: secondaryColor }}>
              Daily Summary Report : {rangeLabel}
            </h2>
          </div>
        </div>

        {/* FILTERS */}
        <div className="no-print" style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>

          {/* ✅ QUICK DATE FILTERS */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
            <button onClick={() => handleQuickDate(0)} style={getQuickBtnStyle(activeQuickFilter === 0)}>Today</button>
            <button onClick={() => handleQuickDate(-1)} style={getQuickBtnStyle(activeQuickFilter === -1)}>Yesterday</button>
            <button onClick={() => handleQuickDate(-7)} style={getQuickBtnStyle(activeQuickFilter === -7)}>Last 7 Days</button>
            <button onClick={() => handleQuickDate(-30)} style={getQuickBtnStyle(activeQuickFilter === -30)}>Last 30 Days</button>
            <button onClick={() => handleQuickDate('all')} style={getQuickBtnStyle(activeQuickFilter === 'all')}>All Time</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setActiveQuickFilter(null);
                }}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }}
              />
            </div>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setActiveQuickFilter(null);
                }}
                style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }}
              />
            </div>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>Type</label>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <label><input type="checkbox" checked={types.sale} onChange={(e) => setTypes({ ...types, sale: e.target.checked })} /> Sale</label>
                <label><input type="checkbox" checked={types.purchase} onChange={(e) => setTypes({ ...types, purchase: e.target.checked })} /> Purchase</label>
                <label><input type="checkbox" checked={types.expense} onChange={(e) => setTypes({ ...types, expense: e.target.checked })} /> Expense</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleGenerate} disabled={loading} style={{ flex: 1, backgroundColor: primaryColor, color: 'white', padding: '0.5rem', borderRadius: '0.375rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>{loading ? 'Loading...' : 'Generate Report'}</button>
              <button onClick={handlePrint} disabled={!hasData} style={{ backgroundColor: secondaryColor, color: 'white', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: !hasData ? 'not-allowed' : 'pointer', opacity: !hasData ? 0.6 : 1 }}>🖨️ Print</button>
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.75rem', fontStyle: 'italic' }}>
            * Summary based on invoice dates for sales/purchases and transaction dates for expenses.
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div style={{ padding: '1rem', color: '#dc3545', textAlign: 'center', backgroundColor: '#fff1f0', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {/* SUMMARY TABLE */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={rowLabelStyle}>Sales Revenue</td>
                <td style={rowValueStyle}>{formatCurrency(summary.sales)}</td>
              </tr>
              <tr>
                <td style={rowLabelStyle}>Cost of Goods Sold (COGS)</td>
                <td style={rowValueStyle}>-{formatCurrency(summary.totalCOGS)}</td>
              </tr>
              <tr>
                <td style={{ ...rowLabelStyle, fontWeight: 'bold', color: '#15803d' }}>Gross Profit</td>
                <td style={{ ...rowValueStyle, fontWeight: 'bold', color: '#15803d' }}>{formatCurrency(grossProfit)}</td>
              </tr>
              <tr>
                <td style={rowLabelStyle}>Expenses</td>
                <td style={rowValueStyle}>-{formatCurrency(summary.expenses)}</td>
              </tr>
              <tr>
                <td style={rowLabelStyle}>Total VAT (Purchase + Sale)</td>
                <td style={rowValueStyle}>{formatCurrency(summary.totalVat)}</td>
              </tr>
              <tr>
                <td style={rowLabelStyle}>Total Discount (Purchase + Sale)</td>
                <td style={rowValueStyle}>{formatCurrency(summary.totalDiscount)}</td>
              </tr>
              <tr>
                <td style={rowLabelStyle}>Total Paid</td>
                <td style={rowValueStyle}>{formatCurrency(summary.totalPaid)}</td>
              </tr>
              <tr>
                <td style={{ ...rowLabelStyle, borderTop: `2px solid ${primaryColor}`, paddingTop: '16px', fontSize: '15px' }}>
                  Total Outstanding
                </td>
                <td style={{
                  ...rowValueStyle,
                  borderTop: `2px solid ${primaryColor}`,
                  paddingTop: '16px',
                  fontSize: '16px',
                  color: summary.totalOutstanding > 0 ? '#dc3545' : '#28a745'
                }}>
                  {formatCurrency(summary.totalOutstanding)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* NET PROFIT CARD */}
        <div style={{ marginTop: '1.5rem', backgroundColor: 'white', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: 0, color: secondaryColor }}>Net Profit (Gross Profit - Expenses)</h3>
          <div style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: netProfit >= 0 ? '#28a745' : '#dc3545',
            marginTop: '0.5rem'
          }}>
            {formatCurrency(netProfit)}
          </div>
        </div>

        <div className="hide-on-print" style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem', color: '#adb5bd' }}>
          Generated on {new Date().toLocaleString()}
        </div>
      </div>

      {/* ==================================================
          PRINT-ONLY A4 REPORT
      ================================================== */}
      <div className="print-only print-page">
        {/* Company header */}
        <div className="print-header">
          <div className="print-header-left">
            {logoUrl && (
              <img src={logoUrl} alt="Logo" className="print-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            )}
            <div className="print-header-text">
              <h1 className="print-company-name">{company?.company_name || 'Company Name'}</h1>
              <p className="print-company-line">{companyAddress || 'No address'}</p>
              {phoneLine && <p className="print-company-line">Tel: {phoneLine}</p>}
              <p className="print-company-line">
                {companyEmail ? `Email: ${companyEmail}` : ''}
                {company?.company_code ? ` · Code: ${company.company_code}` : ''}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">{fiscalYearLabel}-{fiscalYearLabel + 1}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="print-title-block">
          <h2 className="print-report-title">DAILY SUMMARY REPORT</h2>
          <p className="print-report-subtitle">{rangeLabel}</p>
        </div>

        {/* Summary table */}
        <table className="print-table">
          <tbody>
            <tr>
              <td className="print-sum-label">Sales Revenue</td>
              <td className="print-sum-value">{formatCurrency(summary.sales)}</td>
            </tr>
            <tr>
              <td className="print-sum-label">Cost of Goods Sold (COGS)</td>
              <td className="print-sum-value">-{formatCurrency(summary.totalCOGS)}</td>
            </tr>
            <tr>
              <td className="print-sum-label" style={{ fontWeight: 'bold', color: '#15803d' }}>Gross Profit</td>
              <td className="print-sum-value" style={{ fontWeight: 'bold', color: '#15803d' }}>{formatCurrency(grossProfit)}</td>
            </tr>
            <tr>
              <td className="print-sum-label">Expenses</td>
              <td className="print-sum-value">-{formatCurrency(summary.expenses)}</td>
            </tr>
            <tr>
              <td className="print-sum-label">Total VAT (Purchase + Sale)</td>
              <td className="print-sum-value">{formatCurrency(summary.totalVat)}</td>
            </tr>
            <tr>
              <td className="print-sum-label">Total Discount (Purchase + Sale)</td>
              <td className="print-sum-value">{formatCurrency(summary.totalDiscount)}</td>
            </tr>
            <tr>
              <td className="print-sum-label">Total Paid</td>
              <td className="print-sum-value">{formatCurrency(summary.totalPaid)}</td>
            </tr>
            <tr className="print-sum-highlight">
              <td className="print-sum-label">TOTAL OUTSTANDING</td>
              <td className="print-sum-value">{formatCurrency(summary.totalOutstanding)}</td>
            </tr>
          </tbody>
        </table>

        {/* Net card */}
        <div className="print-net-card">
          <div className="print-net-label">Net Profit (Gross Profit - Expenses)</div>
          <div className={`print-net-value ${netProfit >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(netProfit)}
          </div>
        </div>

        {/* Signature footer */}
        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      <style>{`
        .print-only { display: none; }

        @page {
          size: A4 portrait;
          margin: 12mm 14mm 14mm 14mm;
        }

        @media print {
          .no-print { display: none !important; }
          .hide-on-print { display: none !important; }
        }

        @media print {
          html, body {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }

          body * { visibility: hidden !important; }
          .print-only, .print-only * { visibility: visible !important; }

          .print-only {
            position: absolute !important;
            left: 0; top: 0;
            width: 100%;
            display: block !important;
            padding: 0; margin: 0;
            background: #fff !important;
          }

          .screen-only { display: none !important; }

          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 4mm;
            border-bottom: 1.5pt solid #222;
            margin-bottom: 5mm;
          }
          .print-header-left { display: flex; align-items: center; gap: 6mm; flex: 1; }
          .print-logo { max-height: 18mm; max-width: 26mm; object-fit: contain; }
          .print-company-name { font-size: 14pt; font-weight: 700; color: #170f48; margin: 0 0 1mm 0; }
          .print-company-line { font-size: 8.5pt; color: #444; margin: 0.4mm 0; }
          .print-header-right { text-align: right; font-size: 8.5pt; }
          .print-meta-item { display: flex; justify-content: flex-end; gap: 4mm; margin-bottom: 1mm; }
          .print-meta-label { color: #666; }
          .print-meta-value { font-weight: 700; min-width: 26mm; text-align: right; color: #111; }

          .print-title-block { text-align: center; margin-bottom: 6mm; }
          .print-report-title { font-size: 14pt; font-weight: 700; letter-spacing: 2pt; color: #170f48; margin: 0 0 1mm 0; }
          .print-report-subtitle { font-size: 9.5pt; color: #555; margin: 0; }

          /* Summary table */
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10pt;
            max-width: 130mm;
            margin: 0 auto;
          }
          .print-table td {
            padding: 2.5mm 3mm;
            border-bottom: 0.3pt solid #e0e4ea;
          }
          .print-sum-label {
            text-align: left;
            font-weight: 600;
            color: #170f48;
          }
          .print-sum-value {
            text-align: right;
            font-weight: 700;
            color: #111;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }
          .print-sum-highlight td {
            border-top: 1pt solid #170f48;
            border-bottom: 1pt solid #170f48;
            background: #f4f4f4 !important;
            font-weight: 800;
            font-size: 10.5pt;
            padding-top: 3mm;
            padding-bottom: 3mm;
          }

          /* Net card */
          .print-net-card {
            margin: 8mm auto 0;
            padding: 5mm 6mm;
            max-width: 130mm;
            border: 1pt solid #170f48;
            border-radius: 3mm;
            text-align: center;
            background: #f8fafc !important;
          }
          .print-net-label {
            font-size: 9.5pt;
            text-transform: uppercase;
            letter-spacing: 0.5pt;
            color: #170f48;
            font-weight: 700;
            margin-bottom: 2mm;
          }
          .print-net-value {
            font-size: 18pt;
            font-weight: 800;
            font-variant-numeric: tabular-nums;
          }
          .print-net-value.positive { color: #15803d; }
          .print-net-value.negative { color: #b91c1c; }

          /* Footer */
          .print-footer { margin-top: 12mm; page-break-inside: avoid; }
          .print-footer-row {
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            margin-bottom: 5mm;
          }
          .print-signature { flex: 1; text-align: center; font-size: 8.5pt; color: #333; }
          .print-signature-line { border-top: 0.5pt solid #333; margin-bottom: 1.5mm; height: 10mm; }
          .print-footer-note { text-align: center; font-size: 7.5pt; color: #888; margin: 0; }
        }
      `}</style>
    </div>
  );
};

// ✅ Shared table row styles (screen only)
const rowLabelStyle = {
  padding: '12px 20px',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '14px',
  fontWeight: 600,
  color: '#2e3192',
  textAlign: 'left',
};

const rowValueStyle = {
  padding: '12px 20px',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '14px',
  fontWeight: 700,
  color: '#111827',
  textAlign: 'right',
};

// ✅ Quick Date Button Style Function
const getQuickBtnStyle = (isActive) => ({
  padding: '6px 16px',
  backgroundColor: isActive ? '#2e3192' : '#ffffff',
  border: `1px solid ${isActive ? '#2e3192' : '#ced4da'}`,
  borderRadius: '20px', // Pill shape
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '600',
  color: isActive ? '#ffffff' : '#495057',
  transition: 'all 0.2s ease-in-out',
  boxShadow: isActive ? '0 2px 6px rgba(46, 49, 146, 0.3)' : 'none',
});

export default DailySummary;
